import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { IMask, IMaskInput } from 'react-imask';

/**
 * Mesma sintaxe de definição do react-input-mask (já em produção, vinda do
 * cadastro de tipos de índice e de máscaras fixas como CPF/CNPJ/celular):
 * 9 = número, a = letra, * = número ou letra. `\` escapa um desses
 * caracteres para virar literal fixo (ex.: "(99) \9 9999-9999").
 */
export const DEFINICOES_MASCARA = {
    '9': /[0-9]/,
    a: /[A-Za-z]/,
    '*': /[A-Za-z0-9]/,
};

/**
 * Converte um valor CRU (sem os literais da máscara) para o valor MASCARADO
 * correspondente, sem precisar de um input real na tela — útil para telas
 * que guardam/recebem o valor já sem a máscara (ex.: índices).
 */
export const paraValorMascarado = (valorCru: string, mask: string): string =>
    IMask.pipe(valorCru ?? '', { mask, definitions: DEFINICOES_MASCARA }, IMask.PIPE_TYPE.UNMASKED, IMask.PIPE_TYPE.MASKED);

interface IInputComMascaraProps {
    mask: string;
    aoAceitar: (valorMascarado: string, valorCru: string) => void;
}

const InputComMascara = React.forwardRef<HTMLInputElement, IInputComMascaraProps & Record<string, unknown>>(
    ({ mask, aoAceitar, ...rest }, ref) => (
        <IMaskInput
            {...rest}
            mask={mask}
            definitions={DEFINICOES_MASCARA}
            inputRef={ref}
            overwrite={false}
            onAccept={(valorMascarado: unknown, maskRef: { unmaskedValue: string }) =>
                aoAceitar(String(valorMascarado), maskRef.unmaskedValue)
            }
        />
    ),
);

export interface ICampoTextoMascaradoProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
    /** Máscara no padrão 9/a/*, igual à já usada hoje (ex.: item.opcoes de um índice, ou uma máscara fixa). */
    mask: string;
    /** Sempre o valor MASCARADO (com os literais da máscara já aplicados). */
    value: string;
    /** Chamado a cada alteração aceita, com o valor mascarado e o valor cru (sem literais), para a tela escolher qual usar. */
    onChange: (valorMascarado: string, valorCru: string) => void;
}

export const CampoTextoMascarado: React.FC<ICampoTextoMascaradoProps> = ({
    mask,
    value,
    onChange,
    InputProps,
    inputProps,
    ...rest
}) => {
    return (
        <TextField
            {...rest}
            value={value}
            InputProps={{
                ...InputProps,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                inputComponent: InputComMascara as any,
            }}
            inputProps={{
                ...inputProps,
                mask,
                aoAceitar: onChange,
            }}
        />
    );
};
